import { LlmStreamMetadataDto } from '@@types/api.types';
import { Button, type ButtonProps } from '@components/ui/button';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import {
  useAcceptLlmSuggestionMutation,
  useLlmModelsQueryOptions,
  useRejectLlmSuggestionMutation,
} from '@queries/llm.query';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

type FormSuggestionProps = {
  className?: string;
  suggestion?: string;
  onAccept: () => void;
  onReject: () => void;
  acceptButtonProps?: Partial<ButtonProps>;
  rejectButtonProps?: Partial<ButtonProps>;
  /**
   * The AI model used to generate the suggestion
   */
  model?: string;
  /**
   * Whether the suggestion is currently being streamed
   */
  isStreaming?: boolean;
};

// =============================================================================
// CUSTOM HOOK
// =============================================================================

/**
 * Custom hook for managing form suggestion state
 * Supports both text streams and JSON data
 */
export const useFormSuggestion = <TKey extends string | number | symbol, TData = string>(
  onScrollToField?: (key: TKey) => void,
) => {
  const [suggestions, setSuggestions] = React.useState<Partial<Record<TKey, string | TData>>>({});
  const [suggestionMetadata, setSuggestionMetadata] = React.useState<Partial<Record<TKey, LlmStreamMetadataDto>>>({});
  const [streamingStatus, setStreamingStatus] = React.useState<Partial<Record<TKey, boolean>>>({});
  const [generatingStatus, setGeneratingStatus] = React.useState<Partial<Record<TKey, boolean>>>({});
  const [logIds, setLogIds] = React.useState<Partial<Record<TKey, string>>>({});
  const acceptSuggestionMutation = useAcceptLlmSuggestionMutation();
  const rejectSuggestionMutation = useRejectLlmSuggestionMutation();

  const handleMetadata = React.useCallback((params: { key: TKey; metadata: LlmStreamMetadataDto }) => {
    const { key, metadata } = params;
    setSuggestionMetadata((prev) => ({ ...prev, [key]: metadata }));
    if (metadata.logId) {
      setLogIds((prev) => ({ ...prev, [key]: metadata.logId }));
    }
  }, []);

  const handleSuggestionStream = React.useCallback((params: { key: TKey; text: string }) => {
    const { key, text } = params;
    setSuggestions((prev) => ({ ...prev, [key]: text }));
    // Mark as streaming when we receive text
    setStreamingStatus((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleStreamingStart = React.useCallback((key: TKey) => {
    setStreamingStatus((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleStreamingEnd = React.useCallback((key: TKey) => {
    setStreamingStatus((prev) => ({ ...prev, [key]: false }));
  }, []);

  const handleGeneratingStart = React.useCallback((key: TKey) => {
    setGeneratingStatus((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleGeneratingEnd = React.useCallback((key: TKey) => {
    setGeneratingStatus((prev) => ({ ...prev, [key]: false }));
  }, []);

  const handleJsonData = React.useCallback((params: { key: TKey; data: TData }) => {
    const { key, data } = params;
    setSuggestions((prev) => ({ ...prev, [key]: data }));
  }, []);

  const clearSuggestion = React.useCallback(
    (key: TKey) => {
      setSuggestions((prev) => ({ ...prev, [key]: undefined }));
      setSuggestionMetadata((prev) => ({ ...prev, [key]: undefined }));
      setStreamingStatus((prev) => ({ ...prev, [key]: false }));
      setGeneratingStatus((prev) => ({ ...prev, [key]: false }));
      setLogIds((prev) => ({ ...prev, [key]: undefined }));
      onScrollToField?.(key);
    },
    [onScrollToField],
  );

  const acceptSuggestion = React.useCallback(
    async (key: TKey, onAccept: (data: string | TData, meta?: { llmLogId?: string }) => void) => {
      const data = suggestions[key];
      const logId = logIds[key];
      if (!data) return;

      // Pass the generating log id so callers can record AI provenance (ADR-0007).
      // Clear before await so UI resets immediately; the accept-log call is best-effort.
      onAccept(data, { llmLogId: logId });
      clearSuggestion(key);

      if (logId) {
        try {
          await acceptSuggestionMutation.mutateAsync(logId);
        } catch (error) {
          console.warn('Error marking suggestion as accepted:', error);
          errorToast({ description: 'Suggestion applied, but logging the acceptance failed.' });
        }
      }
    },
    [suggestions, logIds, clearSuggestion, acceptSuggestionMutation],
  );

  const rejectSuggestion = React.useCallback(
    async (key: TKey) => {
      const logId = logIds[key];

      // Clear before await so UI resets immediately; the reject-log call is best-effort.
      clearSuggestion(key);

      if (logId) {
        try {
          await rejectSuggestionMutation.mutateAsync(logId);
        } catch (error) {
          console.warn('Error marking suggestion as rejected:', error);
          errorToast({ description: 'Suggestion dismissed, but logging the rejection failed.' });
        }
      }
    },
    [logIds, clearSuggestion, rejectSuggestionMutation],
  );

  const getSuggestion = React.useCallback(
    (key: TKey) => {
      return suggestions[key];
    },
    [suggestions],
  );

  const getSuggestionModel = React.useCallback(
    (key: TKey) => {
      const { actualModel, requestedModel } = suggestionMetadata[key] || {};
      return actualModel || requestedModel;
    },
    [suggestionMetadata],
  );

  const getIsStreaming = React.useCallback(
    (key: TKey) => {
      return Boolean(streamingStatus[key]);
    },
    [streamingStatus],
  );

  const getIsGenerating = React.useCallback(
    (key: TKey) => {
      return Boolean(generatingStatus[key]);
    },
    [generatingStatus],
  );

  const hasSuggestion = React.useCallback(
    (key: TKey) => {
      return Boolean(suggestions[key]);
    },
    [suggestions],
  );

  const clearAllSuggestions = React.useCallback(() => {
    setSuggestions({});
    setSuggestionMetadata({});
    setStreamingStatus({});
    setGeneratingStatus({});
    setLogIds({});
  }, []);

  return {
    suggestions,
    handleSuggestionStream,
    handleMetadata,
    handleStreamingStart,
    handleStreamingEnd,
    handleGeneratingStart,
    handleGeneratingEnd,
    handleJsonData,
    clearSuggestion,
    acceptSuggestion,
    rejectSuggestion,
    getSuggestion,
    getSuggestionModel,
    getIsStreaming,
    getIsGenerating,
    hasSuggestion,
    clearAllSuggestions,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Component that displays the AI model name using the proper label from the API
 */
export const ModelDisplay: React.FC<{ model?: string }> = ({ model }) => {
  const llmModelsQueryOptions = useLlmModelsQueryOptions();
  const { data: llmModels } = useQuery(llmModelsQueryOptions);

  const modelLabel = React.useMemo(() => {
    // Handle cases where no model is selected
    if (!model || model === 'auto') {
      return 'Auto';
    }

    if (!llmModels) return model;
    const foundModel = llmModels.find((m) => m.value === model);
    return foundModel?.label || model;
  }, [llmModels, model]);

  return <span>Model used: {modelLabel}</span>;
};

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * FormSuggestion component for displaying AI-generated suggestions with accept/reject actions
 */
export const FormSuggestion: React.FC<FormSuggestionProps> = (props) => {
  const {
    className,
    suggestion,
    onAccept,
    onReject,
    acceptButtonProps = {},
    rejectButtonProps = {},
    model,
    isStreaming = false,
  } = props;

  if (!suggestion) {
    return null;
  }

  const {
    children: acceptButtonChildren = 'Accept',
    variant: acceptButtonVariant = 'primary',
    size: acceptButtonSize = 'sm',
    onClick: acceptButtonOnClick,
    ...acceptButtonRest
  } = acceptButtonProps;

  const {
    children: rejectButtonChildren = 'Reject',
    variant: rejectButtonVariant = 'ghost',
    size: rejectButtonSize = 'sm',
    onClick: rejectButtonOnClick,
    ...rejectButtonRest
  } = rejectButtonProps;

  return (
    <div className={cn('mt-2 p-4 bg-blue-50 border border-blue-300 rounded-lg', className)}>
      <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{suggestion}</div>
      <div className="flex justify-between items-center mt-4">
        <div className="text-xs text-gray-500 font-medium">
          <ModelDisplay model={model} />
        </div>
        {!isStreaming && (
          <div className="flex gap-2">
            <Button
              size={rejectButtonSize}
              variant={rejectButtonVariant}
              onClick={(e) => {
                onReject();
                rejectButtonOnClick?.(e);
              }}
              {...rejectButtonRest}
            >
              {rejectButtonChildren}
            </Button>
            <Button
              size={acceptButtonSize}
              variant={acceptButtonVariant}
              onClick={(e) => {
                onAccept();
                acceptButtonOnClick?.(e);
              }}
              {...acceptButtonRest}
            >
              {acceptButtonChildren}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
